import React, {
    createContext,
    useContext,
    useState,
    useMemo,
    useEffect,
} from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { AppRoutes } from '../Constants';
import { useEngagement } from './EngagementContext';
import { useGetProjectsForEntity } from '../api/generated/endpoint';
import type { ProjectListItemResponse } from '../api/generated/models';

interface ProjectContextValue {
    activeProjectId: number | undefined;
    activeVersion: number | undefined;
    setActiveProjectId: (id: number | undefined) => void;
    setActiveVersion: (version: number | undefined) => void;
    projects: ProjectListItemResponse[] | undefined;
    isLoadingProjects: boolean;
}

const ProjectContext = createContext<ProjectContextValue | undefined>(
    undefined
);

interface ProjectProviderProps {
    children: React.ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({
    children,
}) => {
    const location = useLocation();
    const { selectedEntityId, activeTimeId } = useEngagement();

    const isOnProjectRoute = !!matchPath(
        { path: AppRoutes.ProjectData, end: false },
        location.pathname
    );

    const { data: projects, isLoading: isLoadingProjects } =
        useGetProjectsForEntity(
            { entityId: selectedEntityId, timeId: activeTimeId },
            {
                query: {
                    enabled:
                        isOnProjectRoute &&
                        selectedEntityId !== undefined &&
                        activeTimeId !== undefined,
                },
            }
        ) as {
            data: ProjectListItemResponse[] | undefined;
            isLoading: boolean;
        };

    const [activeProjectId, setActiveProjectId] = useState<
        number | undefined
    >();
    const [activeVersion, setActiveVersion] = useState<number | undefined>();

    // Auto-select first project when projects load and nothing is selected
    useEffect(() => {
        if (
            isOnProjectRoute &&
            projects &&
            projects.length > 0 &&
            activeProjectId === undefined
        ) {
            setActiveProjectId(projects[0].projectID);
            setActiveVersion(projects[0].activeVersion ?? 1);
        }
    }, [isOnProjectRoute, projects, activeProjectId]);

    // Reset selection when entity/time changes
    useEffect(() => {
        setActiveProjectId(undefined);
        setActiveVersion(undefined);
    }, [selectedEntityId, activeTimeId]);

    const value = useMemo<ProjectContextValue>(
        () => ({
            activeProjectId,
            activeVersion,
            setActiveProjectId,
            setActiveVersion,
            projects,
            isLoadingProjects,
        }),
        [activeProjectId, activeVersion, projects, isLoadingProjects]
    );

    return (
        <ProjectContext.Provider value={value}>
            {children}
        </ProjectContext.Provider>
    );
};

export const useProject = (): ProjectContextValue => {
    const context = useContext(ProjectContext);
    if (!context) {
        return {
            activeProjectId: undefined,
            activeVersion: undefined,
            setActiveProjectId: () => undefined,
            setActiveVersion: () => undefined,
            projects: undefined,
            isLoadingProjects: false,
        };
    }
    return context;
};

export default ProjectContext;
